// ArChat Auto-Normalized File
// Created: 12-03-2026 22:38:36
// Author: SIVOL® - Aldo Frias - Argentina

/**
 * Autor: SIVOL® - Aldo Frias - Argentina
 * Fecha: 11/03/2026 22:50 (Argentina - UTC-3)
 * @file LogoutService.js
 * @description Servicio centralizado de cierre de sesión: broadcast, API logout, limpieza de cachés y storage sin borrar tema/idioma.
 */

import { BASE_PATH, STORAGE_KEYS, joinApiOrigin } from "../../utils/constants";
import {
  restoreContactsManualOrderEntries,
  snapshotContactsManualOrderEntries,
} from "../../utils/contactsManualOrderStorage";
import { DASHBOARD_PREFS_STORAGE_KEY } from "../../utils/dashboardPreferences";
import { broadcastLogout } from "./AuthBroadcast";
import { getToken } from "../apiClient";
import { clearApiCaches } from "../api";
import { logEvent } from "../../utils/runtimeLogger";

let logoutBroadcasted = false;

/** Claves de preferencia UI que deben sobrevivir al cierre de sesión (coherente con AuthContext.clearSession). */
function snapshotUiPreferences() {
  const keys = [
    STORAGE_KEYS.THEME,
    STORAGE_KEYS.LANGUAGE,
    STORAGE_KEYS.REGION,
    "archat_lang",
    "archat_terms_accepted",
    DASHBOARD_PREFS_STORAGE_KEY,
  ];
  const out = {};
  try {
    for (const k of keys) {
      const v = localStorage.getItem(k);
      if (v != null && String(v) !== "") out[k] = v;
    }
  } catch (_) {}
  return out;
}

function restoreUiPreferences(prefs) {
  if (!prefs || typeof prefs !== "object") return;
  try {
    Object.entries(prefs).forEach(([k, v]) => {
      if (v != null) localStorage.setItem(k, String(v));
    });
  } catch (_) {}
}

/**
 * POST a logout.php con cuerpo JSON seguro (evita fallo si la respuesta no es JSON).
 * @param {string} logoutUrl
 */
async function postLogoutSafe(logoutUrl) {
  const headers = {
    "Content-Type": "application/json",
    "X-Requested-With": "XMLHttpRequest",
  };
  const tok = getToken();
  if (tok) headers.Authorization = `Bearer ${tok}`;
  try {
    const res = await fetch(logoutUrl, {
      method: "POST",
      credentials: "include",
      headers,
      body: JSON.stringify({}),
    });
    const text = await res.text();
    if (!text || !String(text).trim()) return { success: res.ok };
    try {
      return JSON.parse(text);
    } catch (_) {
      return { success: res.ok, raw: text.slice(0, 200) };
    }
  } catch (err) {
    console.warn("[ARCHAT][LOGOUT] fetch logout failed", err);
    return { success: false };
  }
}

export async function performLogout() {
  if (!logoutBroadcasted) {
    logoutBroadcasted = true;
    console.log("[ARCHAT][LOGOUT] broadcasting logout");
    broadcastLogout();
  }

  console.log("[ARCHAT][LOGOUT] starting");

  if (typeof console !== "undefined") {
    console.warn("[BASE_PATH_USED]", BASE_PATH);
  }

  clearApiCaches();

  const prefs = snapshotUiPreferences();
  let manualOrderSnap = {};
  try {
    manualOrderSnap = snapshotContactsManualOrderEntries(localStorage);
  } catch (_) {
    manualOrderSnap = {};
  }

  try {
    const logoutUrl = joinApiOrigin("/api/auth/logout.php");
    logEvent("API_ENDPOINT_FIXED", "LogoutService.performLogout", { endpoint: logoutUrl });
    await postLogoutSafe(logoutUrl);
  } catch (err) {
    console.warn("[ARCHAT][LOGOUT] backend logout failed", err);
  }

  try {
    localStorage.clear();
    restoreUiPreferences(prefs);
    restoreContactsManualOrderEntries(localStorage, manualOrderSnap);
  } catch (_) {}

  try {
    sessionStorage.clear();
  } catch (_) {}

  try {
    if (window.socketService) {
      window.socketService.disconnect();
    }
  } catch (_) {}

  console.log("[ARCHAT][LOGOUT] redirecting");

  const redirectUrl = `${window.location.origin}${BASE_PATH}/login`;
  if (typeof console !== "undefined") {
    console.log("[HARD_REDIRECT_FIXED] LogoutService window.location →", redirectUrl);
  }

  setTimeout(() => {
    window.location.href = redirectUrl;
  }, 50);

  setTimeout(() => {
    window.location.href = redirectUrl;
  }, 250);
}
